import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import moment from 'moment'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import hiveActivityReportApi from '../../../middleware/api/TimeAndAttendance/HiveActivityReport/hiveActivityReportApi'
import { RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  EmployeeDayHiveSheet,
  EmployeeHiveReport,
  GetEmployeeDayHiveSheetProps,
  GetHiveActivityReportProps,
  GetManagerHiveActivityReportResponse,
  HiveActivityReportSliceState,
} from '../../../types/TimeAndAttendance/HiveActivityReport/hiveActivityReportTypes'
import { SelectedView } from '../../../types/TimeAndAttendance/TimeInOfficeReport/timeInOfficeReportTypes'
import { currentMonthDateNew } from '../../../utils/helper'

const getEmployeeHiveActivityReport = createAsyncThunk(
  'hiveActivityReport/getEmployeeHiveActivityReport',
  async (props: GetHiveActivityReportProps, thunkApi) => {
    try {
      return await hiveActivityReportApi.getEmployeeHiveActivityReport(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getEmployeeDayHiveSheetReport = createAsyncThunk(
  'hiveActivityReport/getEmployeeDayHiveSheet',
  async (props: GetEmployeeDayHiveSheetProps, thunkApi) => {
    try {
      return await hiveActivityReportApi.getEmployeeDayHiveSheet(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getManagerHiveActivityReport = createAsyncThunk(
  'hiveActivityReport/getManagerHiveActivityReport',
  async (props: GetHiveActivityReportProps, thunkApi) => {
    try {
      return await hiveActivityReportApi.getManagerHiveActivityReport(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getSearchHiveActivityReport = createAsyncThunk(
  'hiveActivityReport/getSearchHiveActivityReport',
  async (props: GetHiveActivityReportProps, thunkApi) => {
    try {
      return await hiveActivityReportApi.getSearchHiveActivityReport(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialHiveActivityReportSliceState: HiveActivityReportSliceState = {
  selectedDate: currentMonthDateNew(
    localStorage.getItem('employeeCountry') as string,
  ),
  selectedView: 'Me',
  managerHiveActivityReport: { list: [], size: 0 },
  employeeHiveActivityReport: {
    id: 0,
    userName: '',
    firstName: '',
    lastName: '',
    activityTimes: [],
    totalHiveTime: '',
    projectIdentifier: '',
  },
  isLoading: ApiLoadingState.idle,
  monthDisplay: moment(new Date()).format('M/YYYY'),
  employeeDayHiveSheet: [],
  selectOptions: 'FromCurrentMonth',
}

const hiveActivityReportSlice = createSlice({
  name: 'hiveActivityReport',
  initialState: initialHiveActivityReportSliceState,
  reducers: {
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload
    },
    setSelectedView: (state, action) => {
      state.selectedView = action.payload
    },
    setMonthDisplay: (state, action) => {
      state.monthDisplay = action.payload
    },
    setSelectOptions: (state, action) => {
      state.selectOptions = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeHiveActivityReport.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.employeeHiveActivityReport = action.payload
      })
      .addCase(getEmployeeDayHiveSheetReport.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.employeeDayHiveSheet = action.payload
      })
      .addMatcher(
        isAnyOf(
          getEmployeeHiveActivityReport.pending,
          getManagerHiveActivityReport.pending,
          getSearchHiveActivityReport.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getManagerHiveActivityReport.fulfilled,
          getSearchHiveActivityReport.fulfilled,
        ),
        (state, action) => {
          state.isLoading = ApiLoadingState.succeeded
          state.managerHiveActivityReport = action.payload
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.hiveActivityReport.isLoading
const employeeHiveActivityReport = (state: RootState): EmployeeHiveReport =>
  state.hiveActivityReport.employeeHiveActivityReport
const employeeDayHiveSheet = (state: RootState): EmployeeDayHiveSheet[] =>
  state.hiveActivityReport.employeeDayHiveSheet
const managerHiveActivityReport = (
  state: RootState,
): GetManagerHiveActivityReportResponse =>
  state.hiveActivityReport.managerHiveActivityReport
const selectedDate = (state: RootState): string =>
  state.hiveActivityReport.selectedDate
const selectedView = (state: RootState): SelectedView =>
  state.hiveActivityReport.selectedView
const managerReportSize = (state: RootState): number =>
  state.hiveActivityReport.managerHiveActivityReport?.size
const monthDisplay = (state: RootState): string =>
  state.hiveActivityReport.monthDisplay
const getSelectOptions = (state: RootState): string =>
  state.hiveActivityReport.selectOptions

const hiveActivityReportThunk = {
  getEmployeeHiveActivityReport,
  getEmployeeDayHiveSheetReport,
  getManagerHiveActivityReport,
  getSearchHiveActivityReport,
}

const hiveActivityReportSelectors = {
  isLoading,
  employeeHiveActivityReport,
  employeeDayHiveSheet,
  managerHiveActivityReport,
  selectedDate,
  selectedView,
  managerReportSize,
  monthDisplay,
  getSelectOptions,
}

export const hiveActivityReportService = {
  ...hiveActivityReportThunk,
  actions: hiveActivityReportSlice.actions,
  selectors: hiveActivityReportSelectors,
}

export default hiveActivityReportSlice.reducer
